import React, { useState } from 'react';
import { Modal, Typography, Space, Alert, Input, Form } from 'antd';
import { CancelOutlined, WarningOutlined } from '@mui/icons-material';
import { PrimaryButton, OutlineButton } from '../../Components/Buttons/Buttons';
import { getTheme } from '../../../theme/theme';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Modal for confirming quote rejection
 * @param {Object} props - Component properties
 * @param {boolean} props.visible - Whether the modal is visible
 * @param {Function} props.onConfirm - Function called when rejection is confirmed
 * @param {Function} props.onCancel - Function called when modal is cancelled
 * @param {Object} props.order - Order object containing quote information
 */
const QuoteRejectModal = ({
    visible,
    onConfirm,
    onCancel,
    order
}) => {
    const theme = getTheme();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Handle form submission
    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            onConfirm(values.reason);
            form.resetFields();
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle modal cancel
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={
                <Space align="center">
                    <CancelOutlined style={{ color: theme.token.colorError }} />
                    <Title level={4} style={{ margin: 0, color: theme.token.colorText }}>
                        Confirm Quote Rejection
                    </Title>
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={550}
            centered
            style={{
                borderRadius: '12px',
            }}
            styles={{
                body: {
                    padding: '24px',
                    backgroundColor: theme.token.colorBgContainer,
                }
            }}
        >
            <Form form={form} layout="vertical">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {/* Warning Alert */}
                    <Alert
                        message="Quote Rejection Confirmation"
                        description="By rejecting this quote, you are declining to proceed with the order under the current terms. Please provide a reason for the rejection."
                        type="warning"
                        icon={<WarningOutlined />}
                        showIcon
                        style={{
                            backgroundColor: theme.token.colorWarningBg,
                            borderColor: theme.token.colorWarning,
                        }}
                    />

                    {/* Order Details */}
                    <div>
                        <Text strong style={{ color: theme.token.colorText }}>
                            Order ID:
                        </Text>
                        <Text style={{ marginLeft: '8px', color: theme.token.colorTextSecondary }}>
                            {order?.id}
                        </Text>
                    </div>

                    {/* Rejection Reason */}
                    <div>
                        <Form.Item
                            name="reason"
                            label={
                                <Text strong style={{ color: theme.token.colorText }}>
                                    Reason for Rejection
                                </Text>
                            }
                            rules={[
                                {
                                    required: true,
                                    message: 'Please provide a reason for rejecting the quote',
                                },
                                {
                                    min: 10,
                                    message: 'Please provide a more detailed reason (minimum 10 characters)',
                                },
                            ]}
                        >
                            <TextArea
                                placeholder="Please explain why you are rejecting this quote..."
                                rows={4}
                                maxLength={500}
                                showCount
                                style={{
                                    backgroundColor: theme.token.colorBgContainer,
                                    borderColor: theme.token.colorBorder,
                                }}
                            />
                        </Form.Item>
                    </div>

                    {/* Confirmation Message */}
                    <div>
                        <Paragraph style={{ color: theme.token.colorTextSecondary, textAlign: 'center' }}>
                            Are you sure you want to reject this quote? This action will notify
                            the team and may require further discussion.
                        </Paragraph>
                    </div>

                    {/* Action Buttons */}
                    <Space size="middle" style={{ width: '100%', justifyContent: 'center', marginTop: '16px' }}>
                        <OutlineButton
                            onClick={handleCancel}
                            style={{ minWidth: '120px' }}
                            disabled={loading}
                        >
                            Cancel
                        </OutlineButton>
                        <PrimaryButton
                            icon={<CancelOutlined />}
                            onClick={handleSubmit}
                            loading={loading}
                            style={{
                                minWidth: '120px',
                                backgroundColor: theme.token.colorError,
                                borderColor: theme.token.colorError,
                            }}
                        >
                            Reject Quote
                        </PrimaryButton>
                    </Space>
                </Space>
            </Form>
        </Modal>
    );
};

export default QuoteRejectModal; 